import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  CheckSquare, ThumbsUp, ThumbsDown, Clock, User, UsersThree, ShieldCheck, ArrowRight,
  ClipboardText, MagnifyingGlass, Warning, CheckCircle, XCircle, Lightning,
  Eye, CaretRight, Calendar, PaperPlaneTilt, Briefcase, Check, X
} from '@phosphor-icons/react';
import { toast } from 'sonner';

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
}

export default function ApprovalsPage() {
  const [tab, setTab] = useState('access');

  // Access Request approvals state
  const [approvals, setApprovals] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selectedApproval, setSelectedApproval] = useState(null);

  // Certification reviews state
  const [certItems, setCertItems] = useState([]);
  const [certPending, setCertPending] = useState(0);
  const [certCompleted, setCertCompleted] = useState(0);
  const [certFilter, setCertFilter] = useState('pending');
  const [certSearch, setCertSearch] = useState('');
  const [selectedCertItems, setSelectedCertItems] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Load access request approvals
  const loadApprovals = useCallback(async () => {
    try {
      const res = await API.get('/approvals', { params: { status: filter || undefined } });
      setApprovals(res.data.items);
    } catch { toast.error('Failed to load approvals'); }
  }, [filter]);
  useEffect(() => { loadApprovals(); }, [loadApprovals]);

  // Load certification reviews
  const loadCertReviews = useCallback(async () => {
    try {
      const res = await API.get('/certifications/my-reviews', {
        params: { status: certFilter || undefined, search: certSearch || undefined, limit: 100 }
      });
      setCertItems(res.data.items);
      setCertPending(res.data.pending_count);
      setCertCompleted(res.data.completed_count);
    } catch { toast.error('Failed to load certification reviews'); }
  }, [certFilter, certSearch]);
  useEffect(() => { loadCertReviews(); }, [loadCertReviews]);

  // Access request handlers
  const handleApprove = async (id) => {
    try {
      const res = await API.put(`/approvals/${id}/approve`);
      toast.success(res.data?.next_approver ? `Step approved. Next: ${res.data.next_approver}` : 'Approved');
      setSelectedApproval(null);
      loadApprovals();
    } catch { toast.error('Failed to approve'); }
  };

  const handleReject = async (id) => {
    try {
      await API.put(`/approvals/${id}/reject`);
      toast.success('Rejected');
      setSelectedApproval(null);
      loadApprovals();
    } catch { toast.error('Failed to reject'); }
  };

  // Certification handlers
  const handleCertDecision = async (itemId, decision) => {
    try {
      await API.put(`/certifications/items/${itemId}/decide`, { decision });
      toast.success(decision === 'certify' ? 'Access certified' : 'Access revoked');
      loadCertReviews();
    } catch { toast.error('Failed to process decision'); }
  };

  const handleBulkCertDecision = async (decision) => {
    if (selectedCertItems.size === 0) { toast.error('No items selected'); return; }
    setBulkProcessing(true);
    try {
      const res = await API.post('/certifications/items/bulk-decide', {
        item_ids: Array.from(selectedCertItems),
        decision
      });
      toast.success(res.data.message);
      setSelectedCertItems(new Set());
      loadCertReviews();
    } catch { toast.error('Failed to process bulk decision'); }
    finally { setBulkProcessing(false); }
  };

  const toggleCertItem = (id) => {
    const s = new Set(selectedCertItems);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedCertItems(s);
  };

  const toggleAllCert = () => {
    const pending = certItems.filter(i => i.status === 'pending');
    if (selectedCertItems.size === pending.length && pending.length > 0) {
      setSelectedCertItems(new Set());
    } else {
      setSelectedCertItems(new Set(pending.map(i => i.id)));
    }
  };

  const typeColor = (t) => {
    if (t === 'access_request') return 'bg-blue-100 text-blue-700';
    if (t === 'role_renewal') return 'bg-amber-100 text-amber-700';
    if (t === 'jit_access') return 'bg-purple-100 text-purple-700';
    return 'bg-slate-100 text-navy-600';
  };

  const statusIcon = (s) => {
    if (s === 'pending') return <Clock size={14} className="text-amber-500" />;
    if (s === 'approved') return <CheckCircle size={14} className="text-emerald-500" />;
    return <XCircle size={14} className="text-red-500" />;
  };

  const statusBadge = (s) => {
    if (s === 'pending') return 'bg-amber-100 text-amber-700';
    if (s === 'approved') return 'bg-emerald-100 text-emerald-700';
    return 'bg-red-100 text-red-700';
  };

  const getApproverTypeLabel = (type) => {
    switch(type) {
      case 'manager': return "Requester's Manager";
      case 'role_owner': return 'Role Owner';
      case 'specific_identity': return 'Specific Approver';
      default: return type || '';
    }
  };

  const getApproverIcon = (type) => {
    switch(type) {
      case 'manager': return <UsersThree size={12} />;
      case 'role_owner': return <ShieldCheck size={12} />;
      case 'specific_identity': return <User size={12} />;
      default: return null;
    }
  };

  // Extract a short title from the full title
  const shortTitle = (title) => {
    if (!title) return '';
    return title.replace(/^Access Request:\s*/, '').replace(/^JIT Access:\s*/, '');
  };

  const pendingApprovals = approvals.filter(a => a.status === 'pending').length;

  return (
    <div data-testid="approvals-page" className="p-4 sm:p-6 md:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Approvals</h1>
          <p className="text-sm text-navy-500 mt-0.5">Review and manage access requests and certification reviews</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-slate-100 rounded-sm p-0.5">
          <TabsTrigger value="access" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-access-approvals">
            <CheckSquare size={14} className="mr-1.5" /> Access Requests
            {pendingApprovals > 0 && <Badge className="ml-1.5 bg-orange-500 text-white text-[9px] px-1.5 py-0">{pendingApprovals}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="certifications" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-cert-approvals">
            <ClipboardText size={14} className="mr-1.5" /> Certifications
            {certPending > 0 && <Badge className="ml-1.5 bg-orange-500 text-white text-[9px] px-1.5 py-0">{certPending}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ========== ACCESS REQUESTS TAB ========== */}
        <TabsContent value="access" className="mt-4 space-y-4">
          <div className="flex gap-2">
            {['pending', 'approved', 'rejected', ''].map(f => (
              <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)}
                className={`rounded-sm text-xs ${filter === f ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                data-testid={`approval-filter-${f || 'all'}`}
              >
                {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
              </Button>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <div className="h-1.5 bg-orange-500" />
            <Table wrapperClassName="max-h-[calc(100vh-300px)]">
              <TableHeader>
                <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500 w-10"></TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Request</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Type</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Step</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Requester</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Date</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {approvals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-sm text-navy-400">
                      <CheckSquare size={28} className="mx-auto mb-2 text-navy-300" />
                      No {filter} approvals at this time.
                    </TableCell>
                  </TableRow>
                ) : approvals.map(item => (
                  <TableRow
                    key={item.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedApproval(item)}
                    data-testid={`approval-row-${item.id}`}
                  >
                    <TableCell className="w-10">
                      {statusIcon(item.status)}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-navy-900 truncate max-w-[280px]">{shortTitle(item.title)}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[9px] uppercase ${typeColor(item.type)}`}>
                        {item.type?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.total_steps > 1 ? (
                        <span className="text-xs text-navy-600 font-medium">{item.approval_step}/{item.total_steps}</span>
                      ) : (
                        <span className="text-xs text-navy-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-navy-600 truncate max-w-[140px] block">{item.requester_name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-navy-500">{timeAgo(item.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[9px] uppercase ${statusBadge(item.status)}`}>
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {item.status === 'pending' && (
                          <>
                            <Button size="sm" onClick={() => handleApprove(item.id)}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm h-7 px-2 text-[10px]"
                              data-testid={`approve-btn-${item.id}`}
                            >
                              <ThumbsUp size={12} weight="fill" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleReject(item.id)}
                              className="rounded-sm h-7 px-2 text-[10px] border-red-200 text-red-600 hover:bg-red-50"
                              data-testid={`reject-btn-${item.id}`}
                            >
                              <ThumbsDown size={12} weight="fill" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setSelectedApproval(item)}
                          className="rounded-sm h-7 px-2 text-navy-400 hover:text-navy-700"
                          data-testid={`view-btn-${item.id}`}
                        >
                          <Eye size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ========== CERTIFICATIONS TAB ========== */}
        <TabsContent value="certifications" className="mt-4 space-y-4">
          {/* Summary bar */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm bg-amber-100 flex items-center justify-center">
                <Clock size={16} className="text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-navy-900">{certPending}</p>
                <p className="text-[10px] text-navy-500 uppercase">Pending</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-emerald-700" />
              <div>
                <p className="text-lg font-bold text-navy-900">{certCompleted}</p>
                <p className="text-[10px] text-navy-500 uppercase">Completed</p>
              </div>
            </div>
            <div className="flex-1" />
            {selectedCertItems.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-navy-500 font-medium">{selectedCertItems.size} selected</span>
                <Button size="sm" onClick={() => handleBulkCertDecision('certify')} disabled={bulkProcessing}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm h-7 px-3 text-xs" data-testid="bulk-certify-btn">
                  <Check size={12} className="mr-1" /> Certify All
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkCertDecision('revoke')} disabled={bulkProcessing}
                  className="rounded-sm h-7 px-3 text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid="bulk-revoke-btn">
                  <X size={12} className="mr-1" /> Revoke All
                </Button>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="flex gap-2">
              {['pending', 'completed', ''].map(f => (
                <Button key={f} variant={certFilter === f ? 'default' : 'outline'} size="sm" onClick={() => { setCertFilter(f); setSelectedCertItems(new Set()); }}
                  className={`rounded-sm text-xs ${certFilter === f ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                  data-testid={`cert-filter-${f || 'all'}`}
                >
                  {f === 'pending' ? 'Pending' : f === 'completed' ? 'Completed' : 'All'}
                </Button>
              ))}
            </div>
            <div className="relative flex-1 max-w-xs">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy-400" />
              <Input placeholder="Search by name, role, or app..." value={certSearch} onChange={(e) => setCertSearch(e.target.value)}
                className="pl-8 h-8 text-xs rounded-sm" data-testid="cert-search-input" />
            </div>
          </div>

          {/* Certifications Table */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <div className="h-1.5 bg-navy-600" />
            <Table wrapperClassName="max-h-[calc(100vh-360px)]">
              <TableHeader>
                <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                  <TableHead className="w-10">
                    {certFilter === 'pending' && certItems.filter(i => i.status === 'pending').length > 0 && (
                      <Checkbox checked={selectedCertItems.size === certItems.filter(i => i.status === 'pending').length && certItems.filter(i => i.status === 'pending').length > 0}
                        onCheckedChange={toggleAllCert} data-testid="cert-select-all" />
                    )}
                  </TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Identity</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Access</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Campaign</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Due Date</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-sm text-navy-400">
                      <ClipboardText size={28} className="mx-auto mb-2 text-navy-300" />
                      No certification reviews {certFilter ? `with status "${certFilter}"` : ''} assigned to you.
                    </TableCell>
                  </TableRow>
                ) : certItems.map(item => {
                  const isOverdue = item.campaign_due_date && new Date(item.campaign_due_date) < new Date() && item.status === 'pending';
                  return (
                    <TableRow key={item.id} className={`hover:bg-slate-50 transition-colors ${isOverdue ? 'bg-red-50/30' : ''}`} data-testid={`cert-item-${item.id}`}>
                      <TableCell>
                        {item.status === 'pending' && (
                          <Checkbox checked={selectedCertItems.has(item.id)} onCheckedChange={() => toggleCertItem(item.id)} />
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium text-navy-900">{item.identity_name}</p>
                        {item.identity_email && <p className="text-[10px] text-navy-400">{item.identity_email}</p>}
                      </TableCell>
                      <TableCell>
                        {item.role_name && (
                          <div className="flex items-center gap-1.5">
                            <ShieldCheck size={12} className="text-orange-500" />
                            <span className="text-sm text-navy-700">{item.role_name}</span>
                          </div>
                        )}
                        {item.application_name && (
                          <span className="text-xs text-purple-600">{item.application_name}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-navy-700">{item.campaign_name}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {isOverdue && <Warning size={12} className="text-red-500" />}
                          <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-navy-600'}`}>
                            {item.campaign_due_date ? new Date(item.campaign_due_date).toLocaleDateString() : '-'}
                          </span>
                        </div>
                        {isOverdue && <span className="text-[9px] text-red-500 font-bold">OVERDUE</span>}
                      </TableCell>
                      <TableCell>
                        {item.status === 'pending' ? (
                          <Badge className="bg-amber-100 text-amber-700 text-[9px]">Pending</Badge>
                        ) : item.decision === 'certify' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Certified</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-[9px]">Revoked</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.status === 'pending' ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" onClick={() => handleCertDecision(item.id, 'certify')}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm h-7 px-2.5 text-xs"
                              data-testid={`certify-btn-${item.id}`}
                            >
                              <Check size={12} className="mr-1" /> Certify
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleCertDecision(item.id, 'revoke')}
                              className="rounded-sm h-7 px-2.5 text-xs border-red-200 text-red-600 hover:bg-red-50"
                              data-testid={`revoke-btn-${item.id}`}
                            >
                              <X size={12} className="mr-1" /> Revoke
                            </Button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-navy-400">
                            {item.reviewer_name} &middot; {item.decided_at ? new Date(item.decided_at).toLocaleDateString() : ''}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ========== APPROVAL DETAIL POPUP ========== */}
      <Dialog open={!!selectedApproval} onOpenChange={(open) => { if (!open) setSelectedApproval(null); }}>
        <DialogContent className="max-w-lg" data-testid="approval-detail-dialog" aria-describedby="approval-detail-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {selectedApproval && statusIcon(selectedApproval.status)}
              <span className="text-navy-900">Approval Details</span>
            </DialogTitle>
          </DialogHeader>

          {selectedApproval && (
            <div className="space-y-5 pt-2">
              {/* Title */}
              <div>
                <p className="text-base font-semibold text-navy-900">{selectedApproval.title}</p>
                <p id="approval-detail-description" className="text-sm text-navy-500 mt-1">{selectedApproval.description}</p>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                <DetailField icon={PaperPlaneTilt} label="Type">
                  <Badge className={`text-[10px] uppercase ${typeColor(selectedApproval.type)}`}>
                    {selectedApproval.type?.replace('_', ' ')}
                  </Badge>
                </DetailField>

                <DetailField icon={Clock} label="Status">
                  <Badge className={`text-[10px] uppercase ${statusBadge(selectedApproval.status)}`}>
                    {selectedApproval.status}
                  </Badge>
                </DetailField>

                <DetailField icon={User} label="Requester">
                  <span className="text-sm text-navy-700">{selectedApproval.requester_name}</span>
                </DetailField>

                {selectedApproval.identity_name && selectedApproval.is_for_someone_else && (
                  <DetailField icon={User} label="For Identity">
                    <span className="text-sm text-navy-700">{selectedApproval.identity_name}</span>
                  </DetailField>
                )}

                <DetailField icon={Calendar} label="Requested">
                  <span className="text-sm text-navy-600">
                    {selectedApproval.created_at ? new Date(selectedApproval.created_at).toLocaleString() : '-'}
                  </span>
                </DetailField>

                {selectedApproval.decided_at && (
                  <DetailField icon={Calendar} label="Decided">
                    <span className="text-sm text-navy-600">
                      {new Date(selectedApproval.decided_at).toLocaleString()}
                    </span>
                  </DetailField>
                )}
              </div>

              {/* Approval Chain */}
              {selectedApproval.total_steps > 1 && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                  <p className="text-xs font-bold text-navy-700 uppercase tracking-widest mb-3">
                    Approval Chain — Step {selectedApproval.approval_step} of {selectedApproval.total_steps}
                  </p>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: selectedApproval.total_steps }, (_, i) => {
                      const stepNum = i + 1;
                      const isCurrent = stepNum === selectedApproval.approval_step;
                      const isCompleted = stepNum < selectedApproval.approval_step;
                      return (
                        <div key={stepNum} className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            isCompleted ? 'bg-emerald-500 text-white' :
                            isCurrent ? 'bg-orange-500 text-white' :
                            'bg-slate-200 text-navy-500'
                          }`}>
                            {isCompleted ? <Check size={12} weight="bold" /> : stepNum}
                          </div>
                          {stepNum < selectedApproval.total_steps && (
                            <div className={`w-6 h-0.5 ${isCompleted ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectedApproval.approver_type && (
                    <div className="flex items-center gap-1.5 mt-3 text-xs text-navy-600">
                      {getApproverIcon(selectedApproval.approver_type)}
                      <span className="font-medium">{getApproverTypeLabel(selectedApproval.approver_type)}</span>
                      {selectedApproval.approver_name && (
                        <span className="text-navy-400">— {selectedApproval.approver_name}</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Single-step approver info */}
              {selectedApproval.total_steps <= 1 && selectedApproval.approver_type && (
                <div className="flex items-center gap-2 text-xs text-navy-600 bg-slate-50 rounded-sm p-3 border border-slate-200">
                  {getApproverIcon(selectedApproval.approver_type)}
                  <span className="font-medium">{getApproverTypeLabel(selectedApproval.approver_type)}</span>
                  {selectedApproval.approver_name && (
                    <span className="text-navy-400">— {selectedApproval.approver_name}</span>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {selectedApproval.status === 'pending' && (
                <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                  <Button onClick={() => handleApprove(selectedApproval.id)}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm"
                    data-testid="dialog-approve-btn"
                  >
                    <ThumbsUp size={16} weight="fill" className="mr-2" /> Approve
                  </Button>
                  <Button variant="outline" onClick={() => handleReject(selectedApproval.id)}
                    className="flex-1 rounded-sm border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="dialog-reject-btn"
                  >
                    <ThumbsDown size={16} weight="fill" className="mr-2" /> Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailField({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} className="text-navy-400" />
        <span className="text-[10px] text-navy-500 uppercase tracking-widest font-semibold">{label}</span>
      </div>
      {children}
    </div>
  );
}
